import { useCallback, useState } from "react";

import displayToast from "@common/components/Toast/displayToast";

interface Props {
  client_id?: string;
  redirect_uri?: string;
  state?: string;
  scope?: string;
  code_challenge?: string;
  code_challenge_method?: string;
  resource?: string;
}

import { useAuthDeps } from "../Authorize";

export default function useAuthorizeApiConsumer({
  client_id,
  redirect_uri,
  state,
  scope,
  code_challenge,
  code_challenge_method,
  resource,
}: Props) {
  const { authService } = useAuthDeps();
  const [isLoading, setIsLoading] = useState(false);

  const getInfo = useCallback(async () => {
    if (!client_id || !redirect_uri || !code_challenge || !code_challenge_method) return;
    setIsLoading(true);
    try {
      return await authService.checkMcpAuth(client_id, redirect_uri, code_challenge, code_challenge_method);
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: error?.response?.data?.message ?? "Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [client_id, redirect_uri, code_challenge, code_challenge_method, authService]);

  const authorize = async () => {
    if (!client_id || !redirect_uri || !code_challenge) return;
    setIsLoading(true);
    try {
      const res = await authService.authenticateMcp(
        client_id,
        redirect_uri,
        code_challenge,
        code_challenge_method,
        resource,
        state,
        scope,
      );

      // Build the redirect URL with query parameters
      const redirectUrl = new URL(res.data.redirect_uri);
      redirectUrl.searchParams.append("code", res.data.code);
      redirectUrl.searchParams.append("state", res.data.state);

      // Redirect to the external URL
      window.location.href = redirectUrl.toString();
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: error?.response?.data?.message ?? "Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return { getInfo, authorize, isLoading };
}
